import { useTranslation } from 'react-i18next';
import { Dices } from 'lucide-react';

import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import type { RankedQueue, RankedQueueNotice } from '@/hooks/useRankedQueue';
import { queueLabelKey } from '@/lib/qgammon/queueMode';
import { nomeDi } from '@/lib/qgammon/storicoPartite';

/**
 * i18n key + interpolation for a notice.
 *
 * Deliberately NOT an exhaustive switch on the cancel reason: the gameserver's
 * reason list grows and this bundle keeps running long after it was compiled,
 * so an unknown reason must fall through to generic copy — never throw, never
 * print the raw protocol token at a player. `useRankedQueue` already narrows
 * anything the shared types package doesn't recognise to `'unknown'`.
 */
function noticeCopy(
  notice: RankedQueueNotice,
  tr: (key: string) => string,
): {
  key: string;
  params?: Record<string, unknown>;
} {
  if (notice.kind === 'failed') return { key: 'ranked.joinFailed' };
  /**
   * 🔴 IL BACINO SBAGLIATO — e non e' un errore di rete travestito.
   * [M `or43` 09/09, staging]: il server **non valida il payload** e ripiega
   * sul 180/10 rispondendo **200**. ⇒ senza questa frase il giocatore vedrebbe
   * la ricerca fermarsi **senza nessuna ragione**, che e' peggio del difetto.
   * ⚠️ Due frasi e non una: se il server non ha detto in quale coda ci ha
   * messi (`got === null`), **non gliene inventiamo una** — «non lo so» non e'
   * un nome di coda.
   */
  if (notice.kind === 'wrongQueue') {
    /**
     * 🔴 L'asse `rated` ha una frase SUA, e non e' pignoleria: sulla coda libera
     * l'orologio puo' concordare (il server ci ha messi «senza orologio») e la
     * classifica no. La frase coi due nomi direbbe «ti avevamo messo in Senza
     * orologio · fuori classifica, non in Senza orologio · fuori classifica» —
     * vera sull'orologio, falsa sulla sola cosa che conta.
     * ⚠️ E non afferma che la partita sarebbe stata classificata: dice che il
     * server NON HA CONFERMATO che non lo fosse, che e' tutto cio' che si sa
     * quando il campo manca.
     */
    if (notice.axis === 'rated') {
      return {
        key: notice.asked === 'unlimited' ? 'ranked.wrongQueueNotUnranked' : 'ranked.wrongQueueNotRanked',
      };
    }
    return notice.got === null
      ? { key: 'ranked.wrongQueueUnknown' }
      : {
          key: 'ranked.wrongQueue',
          params: {
            asked: tr(queueLabelKey(notice.asked)),
            got: tr(queueLabelKey(notice.got)),
          },
        };
  }
  if (notice.kind === 'atMatchCap') {
    return notice.max != null
      ? { key: 'ranked.atMatchCapN', params: { max: notice.max } }
      : { key: 'ranked.atMatchCap' };
  }
  switch (notice.reason) {
    case 'capped':
      return { key: 'ranked.cancelledCapped' };
    case 'timeout':
      return { key: 'ranked.cancelledTimeout' };
    case 'duplicate':
      return { key: 'ranked.cancelledDuplicate' };
    default:
      return { key: 'ranked.cancelledGeneric' };
  }
}

interface RankedQueueSheetProps {
  queue: RankedQueue;
}

/**
 * Il foglio che segue una ricerca: PERCHE' si e' fermata (l'avviso) o il breve
 * «Trovato!» prima della partita. Guscio come il QueueSheet di QDuels (fondo
 * scurito + foglio arrotondato), autonomo: legge soltanto lo stato di
 * `useRankedQueue`, e chi lo monta tiene la navigazione.
 *
 * 🔴 **La RICERCA non sta piu' qui** (dal 30/09, reference «Anello che respira»,
 * L-2606): il tempo, «Cerco un avversario…» e il nome della coda stanno DENTRO
 * l'anello — o nella pill della coda libera — con «Annulla» sotto
 * (`RankedQueueEntry`). Tenerli anche qui li farebbe dire due volte, e un foglio
 * sopra l'anello coprirebbe proprio la cosa che l'impianto ha voluto mostrare.
 * ⇒ Il foglio si vede SOLO con un avviso o a coppia fatta. In ogni altro stato
 * non disegna niente e non apre nessun livello per il tasto indietro.
 *
 * ⚠️ Gli avvisi restano TUTTI raggiungibili (`noticeCopy`: partite al massimo,
 * annullamenti del server, coda sbagliata sui due assi, ingresso fallito): il
 * server puo' fermare la ricerca da solo, e riportare il giocatore all'anello
 * senza una parola si leggerebbe come un guasto.
 */
export function RankedQueueSheet({ queue }: RankedQueueSheetProps) {
  const { t } = useTranslation('backgammon');
  const { status, paired, notice, mode, cancel, dismissNotice } = queue;

  const isPaired = status === 'paired' && paired !== null;
  // A pending notice takes over the sheet: the search is already stopped.
  // Dismissing it (button or backdrop) closes the sheet — `cancel()` would also
  // fire a pointless DELETE for a queue entry the server has already dropped.
  // A coppia fatta, invece, indietro/fondo = `cancel` come prima: ferma il salto
  // alla partita durante il «Trovato!».
  const stopped = notice ? noticeCopy(notice, t) : null;
  const visible = stopped !== null || isPaired;
  const dismiss = stopped ? dismissNotice : cancel;
  useLivelloIndietro(visible, dismiss);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={stopped ? t('ranked.close') : t('ranked.cancel')}
        onClick={dismiss}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Dices className="h-4 w-4 text-brand" />
            {/* ⚠️ A coppia fatta il titolo e' la CODA, non «Partita classificata»: dalla coda
                libera esce una partita fuori classifica, e il titolo di sempre la chiamerebbe
                col nome sbagliato proprio nell'ultimo istante prima del tavolo. */}
            {stopped
              ? t('ranked.stopped')
              : mode
                ? t(queueLabelKey(mode))
                : t('home.title')}
          </h2>
        </div>

        {stopped ? (
          /* ── the server stopped the search: say why, then get out of the way ── */
          <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-center text-sm font-semibold text-fg">
              {t(stopped.key, stopped.params)}
            </p>
            <button
              type="button"
              onClick={dismissNotice}
              className="w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('ranked.close')}
            </button>
          </div>
        ) : paired ? (
          /* ── paired: brief confirmation before the parent navigates ─────── */
          <div className="flex flex-col items-center gap-4 py-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Dices className="h-6 w-6" />
            </span>
            <p className="text-center text-base font-bold text-fg">
              {/* I-402 (Luca): all'avversario il NOME, mai `lg_<sub>`. Lo `username` del `paired` e' GIA' l'etichetta
                  pubblica (`publicName`, QGammon #110: il nome scelto, o `Player <4 hex>` per un account Logto, mai
                  `lg_…`; per un bot il suo username). `displayName` non e' nel payload ne' nel tipo
                  (`@qlashgeminiz/qgammon-types` 0.19.0: `opponent` = `{ userId, username, elo }`): `nomeDi` lo prende
                  se un giorno arriva (il payload del socket non passa da zod in `useRankedQueue`), altrimenti `username`. */}
              {t('ranked.found', { name: nomeDi(paired.opponent) ?? '' })}
              <span className="ml-1 font-semibold tabular-nums text-brand">
                ({paired.opponent.elo})
              </span>
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
